import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import AppContext from 'context/Context';
import { buildFaultCodeCounts } from './costHelpers';

const BAR_COLOR = '#2980b9';
const SELECTED_BAR_COLOR = '#c0392b';

function getChartOptions(faultCounts, isDark, selectedCode) {
  const textColor = isDark ? '#d8e2ef' : '#495057';
  const axisLineColor = isDark ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6';

  return {
    darkMode: isDark,
    title: {
      text: 'IPF Breakdown',
      left: 'center',
      textStyle: { fontSize: 14, fontWeight: 600, color: textColor }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: params => `<strong>${params[0].name}</strong><br/>Count: ${params[0].value}`
    },
    grid: { left: 40, right: 20, top: 50, bottom: 60, containLabel: true },
    xAxis: {
      type: 'category',
      data: faultCounts.map(f => f.code),
      axisLine: { lineStyle: { color: axisLineColor } },
      axisLabel: { color: textColor, interval: 0, rotate: faultCounts.length > 6 ? 20 : 0 }
    },
    yAxis: {
      type: 'value',
      name: 'Count of FAULT CODE',
      nameTextStyle: { color: textColor },
      minInterval: 1,
      axisLine: { show: false },
      axisLabel: { color: textColor },
      splitLine: { lineStyle: { color: axisLineColor } }
    },
    series: [
      {
        type: 'bar',
        data: faultCounts.map(f => f.count),
        itemStyle: {
          color: params => (selectedCode && faultCounts[params.dataIndex].code === selectedCode ? SELECTED_BAR_COLOR : BAR_COLOR)
        },
        label: { show: true, position: 'top', color: textColor },
        barMaxWidth: 60,
        cursor: 'pointer'
      }
    ]
  };
}

export default function IpfBreakdownChart({ records, selectedCode, onSelectCode }) {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const faultCounts = useMemo(() => buildFaultCodeCounts(records), [records]);
  const options = useMemo(() => getChartOptions(faultCounts, isDark, selectedCode), [faultCounts, isDark, selectedCode]);

  const onEvents = useMemo(
    () => ({
      click: params => {
        if (params?.name) onSelectCode(params.name === selectedCode ? null : params.name);
      }
    }),
    [onSelectCode, selectedCode]
  );

  return (
    <div className="cost-ipf-chart-wrapper" style={{ borderRadius: 6, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4, minHeight: 20 }}>
        {selectedCode && (
          <button
            type="button"
            className="cost-chart-clear-filter-btn"
            style={{ fontSize: 12, border: 'none', background: 'none', cursor: 'pointer', padding: '2px 6px' }}
            onClick={() => onSelectCode(null)}
          >
            Filtered by <strong>{selectedCode}</strong> — clear ×
          </button>
        )}
      </div>
      {faultCounts.length > 0 ? (
        <ReactECharts option={options} style={{ height: 380 }} onEvents={onEvents} />
      ) : (
        <div className="cost-summary-status text-center" style={{ fontSize: 13, padding: '24px 0' }}>
          No IPF breakdown data for this period.
        </div>
      )}
    </div>
  );
}

IpfBreakdownChart.propTypes = {
  records: PropTypes.array.isRequired,
  selectedCode: PropTypes.string,
  onSelectCode: PropTypes.func.isRequired
};
